import { useState } from 'react';
import clsx from 'clsx';
import { releaseWerkOrderVoorHerfacturatie } from '../../lib/invoices';
import { formatCurrency } from '../../lib/format';

interface ReleaseWerkOrderDialogProps {
  isOpen: boolean;
  werkOrderId: string;
  orderNumber: string;
  invoiceName: string;
  /** Nieuw factuurtotaal zoals het nu op de order staat (na de correctie). */
  nieuwTotaalInclBtw: number;
  onClose: () => void;
  onSuccess: () => void;
}

/**
 * Zet een volledig gecrediteerde werkorder terug op "nog te factureren".
 * Bewust een aparte stap met een verplichte reden: het raakt de
 * factuurkoppeling en de betaallinks van de order.
 */
export function ReleaseWerkOrderDialog({
  isOpen,
  werkOrderId,
  orderNumber,
  invoiceName,
  nieuwTotaalInclBtw,
  onClose,
  onSuccess,
}: ReleaseWerkOrderDialogProps) {
  const [reden, setReden] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const canSubmit = reden.trim().length >= 10 && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    const result = await releaseWerkOrderVoorHerfacturatie(werkOrderId, reden.trim());
    setSubmitting(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    onSuccess();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 flex flex-col">
        <div className="px-6 py-4 border-b border-slate-200">
          <h2 className="text-base font-semibold text-slate-900">Order weer factureerbaar maken</h2>
          <p className="text-xs text-slate-500 mt-1">
            Order {orderNumber} — {invoiceName} is volledig gecrediteerd
          </p>
        </div>

        <div className="px-6 py-4 space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm space-y-1.5">
            <p className="text-slate-700">Wat er gebeurt:</p>
            <ul className="text-slate-600 space-y-1 list-disc pl-4">
              <li>
                De koppeling met {invoiceName} gaat los, zodat je een nieuwe factuur kunt maken.
                De creditfactuur en het origineel blijven ongewijzigd staan.
              </li>
              <li>
                Het verrekening-paar van de creditfactuur wordt op de order sluitend gemaakt, zodat
                het saldo van de order klopt bij het nieuwe bedrag.
              </li>
              <li>Openstaande betaallinks worden afgesloten; het oude bedrag staat daarin vast.</li>
            </ul>
            <p className="text-slate-700 pt-1">
              De nieuwe factuur wordt naar verwachting{' '}
              <strong className="tabular-nums">{formatCurrency(nieuwTotaalInclBtw)}</strong>.
            </p>
          </div>

          <label className="block">
            <span className="text-sm font-medium text-slate-700">
              Reden <span className="text-slate-400 font-normal">(komt in de planningsnotities)</span>
            </span>
            <input
              type="text"
              value={reden}
              onChange={(e) => setReden(e.target.value)}
              maxLength={200}
              disabled={submitting}
              placeholder="bv. 'Maten gecorrigeerd na klacht, nieuwe factuur nodig'"
              className="mt-1 w-full border border-slate-300 rounded px-2 py-1.5 text-sm"
            />
            {reden.trim().length > 0 && reden.trim().length < 10 && (
              <span className="text-xs text-slate-500">Minimaal 10 tekens.</span>
            )}
          </label>

          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">
              {error}
            </p>
          )}
        </div>

        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="text-sm text-slate-600 hover:text-slate-900 px-3 py-1.5"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={clsx(
              'text-sm font-medium px-3 py-1.5 rounded',
              !canSubmit
                ? 'bg-slate-200 text-slate-500 cursor-not-allowed'
                : 'bg-indigo-600 text-white hover:bg-indigo-700'
            )}
          >
            {submitting ? 'Bezig…' : 'Vrijgeven'}
          </button>
        </div>
      </div>
    </div>
  );
}
